/**
 * HubSpot Import Wizard
 *
 * Three-step fullscreen wizard for importing data from HubSpot:
 *   1. Connect — OAuth connection prompt (skipped if already connected)
 *   2. Configure — choose which HubSpot record types to import
 *   3. Review — review + override the engine's proposed property mappings
 *
 * Wizard chrome is provided by the base `Wizard` component. On successful
 * create, notifies the parent with the returned import_id so it can hand
 * off to the reused CSV `ReviewModal`. State, effects, and network handlers
 * live in `useHubspotImportWizardController` — this file is the render +
 * step-config layer over that controller.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { Wizard, WizardPollingBody, type WizardStep } from '@/lib/base/wizard';
import type { CsvImportStatus } from '@/lib/csv-import/api';

import { useHubspotStatus } from '../../hooks/useHubspotStatus';
import { HubspotImportConfigureStep } from './HubspotImportConfigureStep';
import { HubspotImportConnectStep } from './HubspotImportConnectStep';
import { HubspotImportReviewStep } from './HubspotImportReviewStep';
import { useHubspotImportWizardController } from './useHubspotImportWizardController';

interface HubspotImportWizardProps {
  orgId: number;
  isOpen: boolean;
  onClose: () => void;
  onImportCreated: (importId: number, status: CsvImportStatus) => void;
}

export function HubspotImportWizard({
  orgId,
  isOpen,
  onClose,
  onImportCreated,
}: HubspotImportWizardProps) {
  const { data: statusData } = useHubspotStatus(orgId);
  const connected = statusData?.connected ?? false;
  const defaultStepId = connected ? 'configure' : 'connect';

  // Key used to remount the base Wizard. The remount exists ONLY to move a user from
  // Connect to Configure the instant their connection status resolves after open — a
  // forward, one-way transition (the base Wizard intentionally ignores defaultStepId
  // changes while open). It must NEVER remount again while the wizard stays open: a
  // transient disconnect/reconnect during an in-flight proposal or import request would
  // otherwise reset the base Wizard's isSubmitting flag while the controller keeps the
  // original request alive — letting the user start a SECOND import, with both jobs
  // calling onImportCreated. We therefore latch the key to 'configure' once connected and
  // only reset it when the wizard is closed (so a since-disconnected user starts on
  // Connect again next open).
  const [wizardKey, setWizardKey] = useState(defaultStepId);
  useEffect(() => {
    if (!isOpen) {
      setWizardKey(defaultStepId);
      return;
    }
    if (defaultStepId === 'configure') {
      setWizardKey('configure');
    }
  }, [isOpen, defaultStepId]);

  const {
    objectTypes,
    objectTypesPending,
    objectTypesError,
    objectTypesForbidden,
    stateRef,
    isDirty,
    handleOpenChange,
    handleCancelPoll,
    handleTypesChange,
    handleActionChange,
    handleRetryObjectTypes,
    handleConfigureBeforeNext,
    handleConfirm,
  } = useHubspotImportWizardController({
    orgId,
    isOpen,
    connected,
    onClose,
    onImportCreated,
  });

  // ---------------------------------------------------------------------------
  // Step render delegates
  // ---------------------------------------------------------------------------

  const renderConnect = useCallback(
    () => <HubspotImportConnectStep orgId={orgId} />,
    [orgId]
  );

  // Advancing to Review dispatches the mapping-proposals request inside
  // onBeforeNext, which holds the wizard's isSubmitting flag and disables
  // every footer CTA and the header close button. Without a polling body the
  // user is trapped until that request resolves/times out; the escape hatch
  // aborts the in-flight request and closes the wizard.
  const renderConfigure = useCallback(
    () => (
      <WizardPollingBody
        title='Analyzing your HubSpot data…'
        description="We're proposing property mappings for the record types you chose. This can take a moment."
        onCancel={handleCancelPoll}
        cancelLabel='Cancel'
      >
        <HubspotImportConfigureStep
          objectTypes={objectTypes}
          isPending={objectTypesPending}
          isError={objectTypesError}
          isForbidden={
            objectTypesForbidden || stateRef.current.proposalsForbidden
          }
          selected={stateRef.current.selectedTypes}
          onChange={handleTypesChange}
          onRetry={handleRetryObjectTypes}
        />
      </WizardPollingBody>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stateRef is a stable ref from useHubspotImportWizardController; always read via stateRef.current at call time, never captured
    [
      objectTypesPending,
      objectTypesError,
      objectTypesForbidden,
      handleRetryObjectTypes,
      objectTypes,
      handleTypesChange,
      handleCancelPoll,
    ]
  );

  const renderReview = useCallback(
    () => (
      <WizardPollingBody
        title='Starting your import…'
        description="We're creating your HubSpot import. This can take a few minutes for a large account."
        onCancel={handleCancelPoll}
      >
        <HubspotImportReviewStep
          proposalsByEntity={stateRef.current.proposalsByEntity}
          loadError={stateRef.current.proposalsLoadError}
          truncatedByEntity={stateRef.current.truncatedByEntity}
          sampledByEntity={stateRef.current.sampledByEntity}
          totalRecordsByEntity={stateRef.current.totalRecordsByEntity}
          coreMappingsByEntity={stateRef.current.coreMappingsByEntity}
          actionByKey={stateRef.current.actionByKey}
          onActionChange={handleActionChange}
          confirmError={stateRef.current.confirmError}
        />
      </WizardPollingBody>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stateRef is a stable ref from useHubspotImportWizardController; always read via stateRef.current at call time, never captured
    [handleCancelPoll, handleActionChange]
  );

  // ---------------------------------------------------------------------------
  // Step config
  // ---------------------------------------------------------------------------

  const steps = useMemo<WizardStep[]>(
    () => [
      {
        id: 'connect',
        label: 'Connect',
        title: 'Connect HubSpot',
        hideFooter: true,
        render: renderConnect,
      },
      {
        id: 'configure',
        label: 'Configure',
        title: 'Choose what to import',
        render: renderConfigure,
        canAdvance: () =>
          !objectTypesPending &&
          !objectTypesError &&
          !objectTypesForbidden &&
          !stateRef.current.proposalsForbidden &&
          stateRef.current.selectedTypes.length > 0,
        onBeforeNext: handleConfigureBeforeNext,
        primaryCta: {
          label: ctx => (ctx.isSubmitting ? 'Loading…' : 'Continue'),
        },
        secondaryCtas: [
          {
            id: 'cancel',
            label: 'Cancel',
            onClick: ({ close }) => close(),
          },
        ],
      },
      {
        id: 'review',
        label: 'Review',
        title: 'Review properties',
        render: renderReview,
        canAdvance: () => true,
        primaryCta: {
          label: ctx => {
            if (!ctx.isSubmitting) {
              return 'Start import';
            }
            return stateRef.current.jobStatus === 'pending'
              ? 'Queued'
              : 'Starting import';
          },
        },
        secondaryCtas: [
          {
            id: 'cancel',
            label: 'Cancel',
            onClick: ({ close }) => close(),
          },
        ],
        maxContentWidth: 900,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stateRef is a stable ref from useHubspotImportWizardController; always read via stateRef.current at call time, never captured
    [
      renderConnect,
      renderConfigure,
      renderReview,
      objectTypesPending,
      objectTypesError,
      objectTypesForbidden,
      handleConfigureBeforeNext,
    ]
  );

  return (
    // key=wizardKey (latched above) remounts only for the one-way Connect→Configure
    // transition when connection resolves after open — never again while open, so an
    // in-flight submission's isSubmitting is never reset out from under it.
    <Wizard
      key={wizardKey}
      mode='fullscreen'
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      steps={steps}
      onConfirm={handleConfirm}
      defaultStepId={defaultStepId}
      isDirty={isDirty}
      dialogAriaLabel='Import from HubSpot'
      instance='hubspot-import'
    />
  );
}

export default HubspotImportWizard;
